
import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Outlet, useNavigate, Link } from 'react-router-dom';
import { NAV_ITEMS } from '../constants';
import { NavItem } from '../types';
import { useRBAC } from '../contexts/RBACContext';
import { useLanguages } from '../contexts/LanguageContext';
import {
  Menu,
  X,
  Bell,
  Search,
  ChevronDown,
  ChevronRight,
  LogOut,
  User,
  Settings
} from 'lucide-react';

const SidebarItem: React.FC<{ 
  item: NavItem; 
  sidebarOpen: boolean; 
  hasPermission: (p?: any) => boolean; 
  closeMobileSidebar: () => void 
}> = ({ item, sidebarOpen, hasPermission, closeMobileSidebar }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const visibleSubmenu = item.submenu?.filter(sub => !sub.permission || hasPermission(sub.permission));
  
  const canSeeParent = item.permission 
    ? hasPermission(item.permission) || (visibleSubmenu && visibleSubmenu.length > 0)
    : !item.submenu || (visibleSubmenu && visibleSubmenu.length > 0);

  if (!canSeeParent) return null;

  const isActive = location.pathname === item.href || (visibleSubmenu && visibleSubmenu.some((sub) => sub.href === location.pathname));

  useEffect(() => {
    if (isActive && visibleSubmenu && visibleSubmenu.length > 0) setIsOpen(true);
  }, [isActive, visibleSubmenu]);

  const handleClick = (e: React.MouseEvent) => {
    if (visibleSubmenu && visibleSubmenu.length > 0) {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else {
      closeMobileSidebar();
    }
  };

  return (
    <div className="flex flex-col mb-1 w-full px-4">
      <Link
        to={item.href}
        onClick={handleClick}
        className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all duration-200 w-full ${isActive
            ? 'bg-blue-50 text-blue-700 font-bold'
            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 group font-medium'
          }`}
      >
        <div className="flex items-center min-w-0">
          {item.icon && <item.icon className={`h-5 w-5 ${isActive ? 'text-blue-700' : 'text-gray-400 group-hover:text-gray-700'} mr-4 shrink-0 transition-colors`} />}
          {sidebarOpen && <span className="truncate">{item.title}</span>}
        </div>
        {sidebarOpen && visibleSubmenu && visibleSubmenu.length > 0 && (
          <ChevronRight className={`h-4 w-4 shrink-0 opacity-20 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
        )}
      </Link>

      {sidebarOpen && isOpen && visibleSubmenu && (
        <div className="flex flex-col pl-10 mt-1 space-y-0.5 mb-2">
          {visibleSubmenu.map((sub) => (
            <Link
              key={sub.title}
              to={sub.href}
              onClick={closeMobileSidebar}
              className={`block px-3 py-1.5 rounded-md text-sm transition-all ${location.pathname === sub.href
                  ? 'text-blue-700 font-bold'
                  : 'text-gray-400 hover:text-gray-900 hover:bg-gray-50'
                }`}
            >
              {sub.title}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { currentUser, hasPermission, logout } = useRBAC();
  const { currentLanguage, dir } = useLanguages();
  const navigate = useNavigate();

  // On mobile, default sidebar to closed
  useEffect(() => {
    if (window.innerWidth < 1024) setSidebarOpen(false);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setIsProfileDropdownOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleLogout = async () => {
    if (window.confirm('Logout?')) {
      try { await logout(); navigate('/admin/login', { replace: true }); } catch (e) { console.error(e); }
    }
  };

  const closeMobileSidebar = () => {
    if (window.innerWidth < 1024) setSidebarOpen(false);
  };

  return (
    <div className="h-screen w-full bg-white flex overflow-hidden font-sans text-gray-900" dir={dir}>
      {/* MOBILE BACKDROP */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-40 lg:hidden transition-opacity" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside 
        className={`bg-white border-r border-gray-100 transition-all duration-300 ease-in-out shrink-0 h-full flex flex-col z-50
        fixed inset-y-0 left-0 lg:relative
        ${sidebarOpen ? 'w-64 translate-x-0 shadow-2xl lg:shadow-none' : 'w-20 -translate-x-full lg:translate-x-0'}
        `}
      >
        {/* LOGO SECTION */}
        <div className="h-24 flex items-center justify-between px-6 bg-white w-full border-b border-gray-50">
          <Link to="/admin" className="flex items-center space-x-3.5">
            <div className="h-9 w-9 bg-blue-600 rounded-lg flex items-center justify-center text-white shrink-0 font-bold text-xl">
              E
            </div>
            {sidebarOpen && (
              <span className="text-2xl font-bold text-blue-600 tracking-tight whitespace-nowrap">
                EsiringetCLOUD
              </span>
            )}
          </Link>
          
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-2 text-gray-400">
            <X size={24} />
          </button>
        </div>

        {/* MENU SECTION */}
        <div className="flex-1 overflow-y-auto pb-40 custom-scrollbar pt-4">
          {NAV_ITEMS.map((item) => (
            <SidebarItem 
              key={item.title} 
              item={item} 
              sidebarOpen={sidebarOpen} 
              hasPermission={hasPermission} 
              closeMobileSidebar={closeMobileSidebar} 
            />
          ))}
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col h-full overflow-hidden min-w-0">
        <header className="h-16 bg-white border-b border-gray-100 flex items-center justify-between px-6 shrink-0 z-30">
          <div className="flex items-center flex-1">
            <button className="p-2 text-gray-400 hover:text-gray-900 transition-colors mr-4" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="relative w-full max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-transparent rounded-lg text-sm focus:bg-white focus:border-gray-100 focus:outline-none transition-all"
              />
            </div>
          </div>

          <div className="flex items-center space-x-7">
            <div className="flex items-center text-sm font-medium text-gray-400 hover:text-gray-900 cursor-pointer uppercase">
               {currentLanguage.code} <ChevronDown size={14} className="ml-1 opacity-40" />
            </div>
            <button className="p-2 text-gray-400 hover:text-gray-900 relative">
               <Bell size={20} />
            </button>
            <div className="relative" ref={dropdownRef}>
              <button onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)} className="flex items-center text-left">
                <div className="hidden sm:flex flex-col items-end mr-3">
                  <span className="text-sm font-bold text-gray-900">{currentUser?.name || 'Admin User'}</span>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                    {currentUser?.roleName || 'SUPER ADMIN'}
                  </span>
                </div>
                <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-400 border border-gray-100 hover:border-blue-200 transition-all">
                  {currentUser?.name?.[0]?.toUpperCase() || 'A'}
                </div>
              </button>
              {isProfileDropdownOpen && (
                <div className="absolute right-0 mt-3 w-48 bg-white rounded-lg shadow-xl py-1 z-[200] border border-gray-100 animate-fade-in text-sm font-medium">
                  <Link to="/admin/admins" className="block px-4 py-2 hover:bg-gray-50">Profile</Link>
                  <Link to="/admin/site-settings" className="block px-4 py-2 hover:bg-gray-50">Settings</Link>
                  <div className="h-px bg-gray-100 my-1"></div>
                  <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50">Logout</button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-10 bg-white">
           <Outlet />
        </main>
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #eee; border-radius: 10px; }
        .animate-fade-in { animation: fadeIn 0.15s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default Layout;
